/**
 * tenant-font-choice — the fonts a studio's client theme may name.
 *
 * The two font tokens (`--client-font-heading`, `--client-font-body`) carry one
 * of these stacks and nothing else: the sanitizer keeps a value only when it
 * names a font listed for that role, and it stores the stack written here, so
 * no operator text ever reaches a page. Each stack falls back to the bundled
 * font of its role first, so a swap never flashes a system face.
 *
 * EB Garamond and Outfit ship with the app (@fontsource, apps/webapp/src/main.tsx);
 * every other font is a file Telify hosts under apps/webapp/public/lib/, which
 * the serve-time doors link only when a resolved theme names it.
 *
 * Contract: docs/data-model/dm-client-theming.md. Pure: no DOM, no DB, no env.
 */
import type { ClientThemeTokens } from './tokens.js';

export type ClientFontRole = 'heading' | 'body';

export interface ClientThemeFont {
  /** The full `font-family` value the token carries. */
  readonly stack: string;
  /** Same-origin stylesheet that declares the faces; null = bundled with the app. */
  readonly stylesheet: string | null;
  /** The roles a studio may pick it for. */
  readonly roles: readonly ClientFontRole[];
}

export const CLIENT_THEME_FONTS = {
  'EB Garamond': {
    stack: "'EB Garamond', Georgia, serif",
    stylesheet: null,
    roles: ['heading'],
  },
  Outfit: {
    stack: "'Outfit', system-ui, -apple-system, sans-serif",
    stylesheet: null,
    roles: ['heading', 'body'],
  },
  // Upright 400 to 600, italic 400 and 500.
  'Cormorant Garamond': {
    stack: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
    stylesheet: '/webapp/lib/cormorant-garamond@v21/cormorant-garamond.css',
    roles: ['heading'],
  },
  // Upright 300 to 600; a 700 request draws the 600 face.
  Jost: {
    stack: "'Jost', 'Outfit', system-ui, -apple-system, sans-serif",
    stylesheet: '/webapp/lib/jost@v20/jost.css',
    roles: ['heading', 'body'],
  },
  // Capitals only, so headings only. 400 to 900.
  Cinzel: {
    stack: "'Cinzel', 'EB Garamond', Georgia, serif",
    stylesheet: '/webapp/lib/cinzel@v26/cinzel.css',
    roles: ['heading'],
  },
  // 400, 700 and 900.
  'Cinzel Decorative': {
    stack: "'Cinzel Decorative', 'Cinzel', 'EB Garamond', Georgia, serif",
    stylesheet: '/webapp/lib/cinzel-decorative@v19/cinzel-decorative.css',
    roles: ['heading'],
  },
} as const satisfies Record<string, ClientThemeFont>;

export type ClientThemeFontName = keyof typeof CLIENT_THEME_FONTS;

/** Today's fonts, the value every preset and look carries. */
export const DEFAULT_CLIENT_FONT_STACKS = {
  '--client-font-heading': CLIENT_THEME_FONTS['EB Garamond'].stack,
  '--client-font-body': CLIENT_THEME_FONTS.Outfit.stack,
} as const;

const FONT_ROLE_BY_TOKEN: Record<string, ClientFontRole> = {
  '--client-font-heading': 'heading',
  '--client-font-body': 'body',
};

const FONTS: readonly ClientThemeFont[] = Object.values(CLIENT_THEME_FONTS);
const FONT_NAMES = Object.keys(CLIENT_THEME_FONTS) as ClientThemeFontName[];

/**
 * The canonical stack for a font token's value, or null when the value is not
 * a font listed for that token's role. Accepts the family name (any case) or
 * the exact stack, so a stored value reads back unchanged.
 */
export function clientFontStack(token: string, value: string): string | null {
  const role = FONT_ROLE_BY_TOKEN[token];
  if (!role) return null;
  const v = value.trim();
  const name = FONT_NAMES.find((n) => n.toLowerCase() === v.toLowerCase());
  const font = name ? CLIENT_THEME_FONTS[name] : FONTS.find((f) => f.stack === v);
  if (!font || !(font.roles as readonly ClientFontRole[]).includes(role)) return null;
  return font.stack;
}

/**
 * The hosted font stylesheets a resolved theme needs, in token order, each once.
 * Only listed stacks can yield a path, so a value that is not on the list loads
 * nothing.
 */
export function hostedFontStylesheets(tokens: Partial<ClientThemeTokens>): string[] {
  const hrefs: string[] = [];
  for (const token of Object.keys(FONT_ROLE_BY_TOKEN)) {
    const value = tokens[token as keyof ClientThemeTokens];
    const font = FONTS.find((f) => f.stack === value);
    if (font?.stylesheet && !hrefs.includes(font.stylesheet)) hrefs.push(font.stylesheet);
  }
  return hrefs;
}
